import {DateTime} from 'luxon';

// Custom Imports
import {SignUpData, CompanySignUpData, SignUpError, SignUpResult} from '../types';

import * as api from '../../../../lib/api/v1/index';
import CookieService from '../../../../services/CookieService';
import {SupportedLanguages} from '../../../../types/SupportedLanguages';
import {User} from '../../../../types/User';
import {ApplicantRoleData, CompanyAdminRoleData} from '../../../../types/User';

export class SignUpService {
  private signUpData: SignUpData;

  constructor(formData: FormData, isCompanySignUp: boolean, preferredLanguage: SupportedLanguages) {
    this.signUpData = isCompanySignUp
      ? this.buildCompanySignUpData(formData, preferredLanguage)
      : this.buildSignUpData(formData, preferredLanguage);
  }

  private buildSignUpData(formData: FormData, language: SupportedLanguages): SignUpData {
    const currentDateTime = DateTime.local();
    const timezone = currentDateTime.zoneName;

    const signUpData: SignUpData = {
      user: {
        preferred_language: language,
        email: formData.get('email') as string,
        password: formData.get('password') as string,
        first_name: formData.get('firstName') as string,
        last_name: formData.get('lastName') as string,
        timezone: timezone as string,
        mobile_phone_number: formData.get('mobilePhoneNumber') as string,
        terms_of_service: {
          accept: Boolean(formData.get('termsOfService')),
          version: '20230425', //TODO: move to constant
        },
        role: 'applicant',
      },
    };

    return signUpData;
  }

  private buildCompanySignUpData(formData: FormData, language: SupportedLanguages): CompanySignUpData {
    const currentDateTime = DateTime.local();
    const timezone = currentDateTime.zoneName;

    const signUpData: CompanySignUpData = {
      user: {
        preferred_language: language,
        company_name: formData.get('companyName') as string,
        mobile_phone_number: formData.get('mobilePhoneNumber') as string,
        email: formData.get('email') as string,
        password: formData.get('password') as string,
        first_name: formData.get('firstName') as string,
        last_name: formData.get('lastName') as string,
        timezone: timezone as string,
        terms_of_service: {
          accept: Boolean(formData.get('termsOfService')),
          version: '20230425', //TODO: move to constant
        },
        role: 'company_admin',
      },
    };

    return signUpData;
  }

  async signUp(): Promise<User | undefined> {
    try {
      const response = await api.createUser(this.signUpData);

      if (response.ok) {
        const json_response = await response.json();

        const applicantRoleData: ApplicantRoleData | undefined = {
          avatarUrl: json_response.user.role.data.avatar_url,
          applicantId: json_response.user.role.data.applicant_id,
          applicantOnboardingId: json_response.user.role.data.applicant_onboarding_id,
          onboardingCompleted: json_response.user.role.data.onboarding_completed,
          talentSurveyId: json_response.user.role.data.talent_survey_id,
        };

        const signedUpUser = {
          id: json_response.user.resource_id,
          applicantId: json_response.user.role.data.applicant_id,
          applicantOnboardingId: json_response.user.role.data.applicant_onboarding_id,
          email: json_response.user.email,
          firstName: json_response.user.first_name,
          lastName: json_response.user.last_name,
          role: {
            type: json_response.user.role.type,
            data: applicantRoleData,
          },
          accountConfirmed: json_response.user.account_confirmed,
          token: json_response.meta.auth_token,
          refreshToken: json_response.meta.refresh_token,
        };

        CookieService.setCookie(signedUpUser);

        return signedUpUser;
      } else {
        return undefined;
      }
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async companySignUp(): Promise<SignUpResult> {
    try {
      const response = await api.createUser(this.signUpData);

      if (response.ok) {
        const json_response = await response.json();

        const roleData: CompanyAdminRoleData = {
          avatarUrl: json_response.user.role.data.avatar_url,
          companyId: json_response.user.role.data.company_id,
          companyAdminId: json_response.user.role.data.company_admin_id,
          contractSigned: json_response.user.role.data.contract_signed,
        };

        const signedUpUser = {
          id: json_response.user.resource_id,
          email: json_response.user.email,
          firstName: json_response.user.first_name,
          lastName: json_response.user.last_name,
          role: {
            type: json_response.user.role.type,
            data: roleData,
          },
          accountConfirmed: json_response.user.account_confirmed,
          token: json_response.meta.auth_token,
          refreshToken: json_response.meta.refresh_token,
        };

        CookieService.setCookie(signedUpUser);

        return {
          success: true,
          user: signedUpUser,
        };
      } else {
        const json_response = await response.json();

        const signUpError: SignUpError = {
          errorCode: json_response.error_code,
          errorMessage: json_response.error_message,
          extraDetails: json_response.extra_details,
        };

        return {
          success: false,
          error: signUpError,
        };
      }
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
